import type { NavigationGuard } from 'vue-router';
import { useAuthStore } from '@/shared/stores/authStore';
import { useProgressStore } from '../stores/progressStore';
import { useCommissioningDeviceStore } from '../stores/commissioningDeviceStore';
import { useDeviceSpecificationStore } from '../stores/deviceSpecificationStore';
import { UserRole } from '@/models/UserRole';
import { AppContext } from '@/models/AppContext';
import { message } from 'ant-design-vue';
import { isCognitoEnabled } from '@/shared/auth/cognitoFeatureFlag';
import { isLoggedIn } from '@/shared/auth/session';
import { isPublicCommissioningEntry } from './utils';

export const comAccessGuard: NavigationGuard = (to, _from, next) => {
  if (
    to.matched.some((record) => record.meta.requiresComAccess) &&
    !(import.meta.env.VITE_ACCESS_COMMISSIONING === 'true')
  ) {
    next({ path: '/' });
  } else {
    next();
  }
};

const _cognitoComAuthGuard: NavigationGuard = async (to, _from, next) => {
  const needsComAuth = to.matched.some((record) => record.meta.requiresComAuth);
  if (!needsComAuth || isPublicCommissioningEntry(to.name)) {
    next();
  } else {
    const loggedIn = await isLoggedIn();
    if (!loggedIn) {
      // Let the main router guard trigger Cognito login with the intended return URL.
      next();
      return;
    }

    const authStore = useAuthStore();

    if (!authStore.isProfileHydrated) {
      const isNonCommissioner =
        authStore.backendProfile !== null &&
        !authStore.backendProfile.roles.includes(UserRole.Commissioner);

      if (isNonCommissioner) {
        next({ name: 'Home' });
        return;
      }

      // Profile still loading, or we already know the user is a commissioner.
      next();
      return;
    }

    if (!authStore.isLoginAtAs(AppContext.ComApp, UserRole.Commissioner)) {
      next({ name: 'Home' });
    } else {
      next();
    }
  }
};

const _legacyComAuthGuard: NavigationGuard = async (to, _from, next) => {
  // Legacy code for in-app authentication. 
  const needsComAuth = to.matched.some((record) => record.meta.requiresComAuth);
  if (!needsComAuth) {
    next();
  } else {
    const authStore = useAuthStore();
    const isLoginPage = to.name === 'ComLogin';
    const isAccountActivatedPage = to.name === 'ComAccountActivated';
    const isPasswordResetPage = to.name === 'ComPasswordReset';
    const isBypassAuth = isLoginPage || isAccountActivatedPage || isPasswordResetPage;

    let normalizedPath = to.path;
    if (normalizedPath.endsWith('/')) {
      normalizedPath = normalizedPath.slice(0, -1);
    }
    const isCommissioningRoot = normalizedPath === '/commissioning';

    if (await isLoggedIn(AppContext.ComApp)) {
      if (!authStore.isLoginAtAs(AppContext.ComApp, UserRole.Commissioner)) {
        authStore.$reset(); // Logout from ComApp (Otherwise, it will be an infinite loop)
        message.error('Not authorised to access');
        next({ name: 'ComLogin' });
      } else {
        if (isLoginPage || isCommissioningRoot) {
          next({ name: 'ComWelcome' });
        } else {
          next();
        }
      }
    } else {
      // Not Logged in at ComApp
      if (isBypassAuth) {
        next();
      } else {
        next({ name: 'ComLogin' });
      }
    }
  }
};

export const comAuthGuard: NavigationGuard = async (to, _from, next) => {
  if (isCognitoEnabled) {
    await _cognitoComAuthGuard(to, _from, next);
  } else {
    await _legacyComAuthGuard(to, _from, next);
  }
};

export const serialNumberGuard: NavigationGuard = (to, _from, next) => {
  const commissioningDeviceStore = useCommissioningDeviceStore();

  const shouldHaveSerialNumber =
    to.matched.some((record) => record.meta.requiresSerialNumber) &&
    commissioningDeviceStore.serialNumber === '';

  if (shouldHaveSerialNumber) {
    next({ name: 'SerialNumberHow' });
  } else {
    next();
  }
};

/**
 * Prevent accessing Configure SolShare routes without a site address.
 * A site address can from the meter record (backend response) or frontend user selected site.
 *
 * Routes that require an address should set one of the following to be true:
 *  - meta.requiresAddressEntry: true, if either a backend retrieved address or a frontend user selected site is sufficient.
 *  - meta.requiresConfirmedAddress: true, if the solshare must have confirmed address in the backend (ie. meter.siteId is assigned)
 *
 * Redirects user back to the Project & Address section if address is missing.
 */
export const addressGuard: NavigationGuard = (to, _from, next) => {
  const needsConfirmedAddress = to.meta.requiresConfirmedAddress === true;
  const needsAddressEntry = to.meta.requiresAddressEntry === true;

  if (!needsAddressEntry && !needsConfirmedAddress) {
    next();
    return;
  }

  const deviceSpecificationStore = useDeviceSpecificationStore();
  const hasSolshareAddress = deviceSpecificationStore.hasAssignedSite;
  const hasSelectedSite = deviceSpecificationStore.hasSelectedSite;

  const shouldRedirectToProjectSelect =
    (needsConfirmedAddress && !hasSolshareAddress) ||
    (needsAddressEntry && !hasSolshareAddress && !hasSelectedSite);

  if (shouldRedirectToProjectSelect) {
    next({ name: 'ProjectSelect' });
  } else {
    next();
  }
};

/**
 * Set current progress step based on route.
 * { meta.progressStep } set on the parent will also works on child routes.
 */
export const commissioningProgress: NavigationGuard = (to) => {
  const hasProgressStep = to.matched.some((record) => record.meta.progressStep);

  if (hasProgressStep) {
    const matchedRecord = to.matched.find((record) => record.meta.progressStep);
    const step = matchedRecord?.meta.progressStep;

    if (typeof step !== 'number' || step < 1) {
      throw new Error('Invalid progress step');
    }
    const progressStore = useProgressStore();
    progressStore.setCurrentStep(step);
  }
};
